import { useEffect, useState } from "react";

import { format } from "date-fns";

import {
  EPISODE_DETAIL_INCLUDE,
  getEpisodeDetailData,
} from "@/api/endpoints/channels";
import { ChannelDetailEpisode } from "@/types/Channel";
import SpinnerAnimated from "@components/SpinnerAnimated";

import styles from "./EpisodeDetails.module.css";

const EpisodeDetailsList: React.FunctionComponent<{
  episode: ChannelDetailEpisode;
}> = ({ episode }) => {
  const djangoAdminURL = `https://allears.ai/admin-allears/channels/episode/${episode?.pk}/`;
  const latest_transcript = episode.latest_transcript;

  return (
    <div className={styles.detailsList}>
      {episode?.pk && (
        <p>
          <span className={styles.detailLabel}>Episode ID: </span>
          {episode?.pk}
        </p>
      )}
      {latest_transcript?.id && (
        <p>
          <span className={styles.detailLabel}>Transcript ID: </span>
          {latest_transcript?.id}
        </p>
      )}
      {episode?.pk && (
        <p>
          <span className={styles.detailLabel}>Django Admin: </span>
          <a href={djangoAdminURL}>{djangoAdminURL}</a>
        </p>
      )}
      {episode.external_media_source && (
        <p>
          <span className={styles.detailLabel}>External media source: </span>
          <a href={episode.external_media_source}>
            {episode.external_media_source}
          </a>
        </p>
      )}
      {episode.internal_media_source && (
        <p>
          <span className={styles.detailLabel}>Internal media source: </span>
          <a href={episode.internal_media_source}>
            {episode.internal_media_source}
          </a>
        </p>
      )}
      {episode.created && (
        <p>
          <span className={styles.detailLabel}>Episode created: </span>
          {format(new Date(episode.created), "PP HH:mm")}
        </p>
      )}
      {latest_transcript?.created && (
        <p>
          <span className={styles.detailLabel}>Transcript created: </span>
          {format(new Date(latest_transcript.created), "PP HH:mm")}
        </p>
      )}
      {latest_transcript?.indexed_elasticsearch && (
        <p>
          <span className={styles.detailLabel}>Transcript indexed: </span>
          {format(
            new Date(latest_transcript.indexed_elasticsearch),
            "PP HH:mm"
          )}
        </p>
      )}
      {latest_transcript?.status && (
        <p>
          <span className={styles.detailLabel}>Status: </span>
          {latest_transcript.status}
        </p>
      )}
      {latest_transcript?.length && (
        <p>
          <span className={styles.detailLabel}>Length: </span>
          {latest_transcript.length}
        </p>
      )}
      {episode.transcript_part_count !== undefined && (
        <p>
          <span className={styles.detailLabel}>Number of parts: </span>
          {episode.transcript_part_count}
        </p>
      )}
      {episode.requested_by_user && (
        <p>
          <span className={styles.detailLabel}>
            Added for transcription by user:{" "}
          </span>
          {episode.requested_by_user.first_name}{" "}
          {episode.requested_by_user.last_name}
        </p>
      )}
    </div>
  );
};

const TranscriptDisplay: React.FunctionComponent<{
  episode: ChannelDetailEpisode;
}> = ({ episode }) => {
  return (
    <div className={styles.transcriptDisplay}>{episode.transcript_text}</div>
  );
};

const EpisodeDetails: React.FunctionComponent<{ id: string }> = ({ id }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [episode, setEpisode] = useState<ChannelDetailEpisode>();

  // Get episode details
  useEffect(() => {
    setLoading(true);
    getEpisodeDetailData(id, [
      EPISODE_DETAIL_INCLUDE.LATEST_TRANSCRIPT,
      EPISODE_DETAIL_INCLUDE.TRANSCRIPT_PART_COUNT,
      EPISODE_DETAIL_INCLUDE.REQUESTED_BY_USER,
      EPISODE_DETAIL_INCLUDE.TRANSCRIPT_TEXT,
    ])
      .then((response) => {
        if (response.ok && response.data) {
          setEpisode(response.data);
        } else {
          throw new Error(response.statusText);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  return (
    <tr>
      <td className={styles.episodeDetails}></td>
      <td className={styles.episodeDetails} colSpan={2}>
        {loading && !episode ? (
          <SpinnerAnimated fill="currentColor" width="2rem" />
        ) : (
          episode && <EpisodeDetailsList episode={episode} />
        )}
      </td>
      <td className={styles.episodeDetails} colSpan={3}>
        {loading && !episode ? (
          <SpinnerAnimated fill="currentColor" width="2rem" />
        ) : (
          episode &&
          episode.transcript_text && <TranscriptDisplay episode={episode} />
        )}
      </td>
    </tr>
  );
};

export default EpisodeDetails;
